"use client";

import {
  type ConsentValue,
  deleteAnalyticsCookies,
  deleteConsent,
  getClientConsent,
  setConsent,
} from "@/lib/analytics/consent";
import { requestDistinctId } from "@/lib/analytics/client";
import { pushConsentUpdate } from "@/lib/analytics/consentMode";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";

/**
 * Broadcast when any `useCookieConsent` instance writes a decision. Each hook
 * keeps its own React state; without this, accepting in the banner leaves a
 * sibling (CloudflareBeacon, EventViewTracker) stuck on its mount-time value.
 */
export const CONSENT_CHANGE_EVENT = "cookie-consent-change";

function dispatchConsentChange(value: ConsentValue | undefined): void {
  window.dispatchEvent(
    new CustomEvent<ConsentValue | undefined>(CONSENT_CHANGE_EVENT, {
      detail: value,
    }),
  );
}

export function useCookieConsent() {
  const router = useRouter();
  const [consent, setConsentState] = useState<ConsentValue | undefined>(
    undefined,
  );
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setConsentState(getClientConsent());
    setLoaded(true);
  }, []);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ConsentValue | undefined>).detail;
      // jsdom/CustomEvent may surface an omitted detail as null.
      setConsentState(detail ?? undefined);
    };
    window.addEventListener(CONSENT_CHANGE_EVENT, handler);
    return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler);
  }, []);

  const accept = useCallback(() => {
    setConsent("accepted");
    setConsentState("accepted");
    pushConsentUpdate("granted");
    // Mint dpid before broadcasting so EventViewTracker /track cannot race a
    // second getOrSetDistinctId and leave the page view on a different id.
    void requestDistinctId().then(() => {
      dispatchConsentChange("accepted");
    });
  }, []);

  const decline = useCallback(() => {
    setConsent("declined");
    deleteAnalyticsCookies();
    setConsentState("declined");
    pushConsentUpdate("denied");
    dispatchConsentChange("declined");
    router.refresh();
  }, [router]);

  const reset = useCallback(() => {
    deleteConsent();
    setConsentState(undefined);
    pushConsentUpdate("denied");
    dispatchConsentChange(undefined);
    router.refresh();
  }, [router]);

  return { consent, loaded, accept, decline, reset };
}
